import React, { useState, useRef, useEffect, useCallback } from "react";
import { resendMfaEmailOtp } from "../../services/apiService";
import {
  switchMfaLoginMethod,
  MFA_METHOD_EMAIL,
  MFA_METHOD_AUTHENTICATOR,
} from "../../services/mfaService";
import "./MfaVerification.scss";

const MFA_METHOD_EMAIL_LEGACY = 1;

function parseApiDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatCountdown(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function pickTiming(challenge, resendData) {
  const source = resendData || challenge || {};
  return {
    otpExpiresAt: parseApiDate(source.OtpExpiresAt ?? source.otpExpiresAt),
    resendAvailableAt: parseApiDate(source.ResendAvailableAt ?? source.resendAvailableAt),
    resendsRemaining:
      source.ResendsRemaining ?? source.resendsRemaining ?? null,
    maxResends: source.MaxResends ?? source.maxResends ?? 5,
  };
}

function MfaVerification({ challenge, onVerify, onBack, loading, error, onChallengeUpdate, onSetupRequired }) {
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [resendLoading, setResendLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState(false);
  const [localChallenge, setLocalChallenge] = useState(challenge);
  const [timing, setTiming] = useState(() => pickTiming(challenge));
  const [now, setNow] = useState(() => Date.now());
  const inputRefs = useRef([]);

  const activeChallenge = localChallenge || challenge;
  const mfaMethod = activeChallenge?.MfaMethod ?? activeChallenge?.mfaMethod ?? MFA_METHOD_EMAIL_LEGACY;
  const isEmail = mfaMethod === MFA_METHOD_EMAIL || mfaMethod === MFA_METHOD_EMAIL_LEGACY;
  const maskedEmail = activeChallenge?.MaskedEmail ?? activeChallenge?.maskedEmail ?? "";

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    setLocalChallenge(challenge);
    setTiming(pickTiming(challenge));
  }, [challenge]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const applyResendTiming = useCallback((data) => {
    if (!data) return;
    setTiming(pickTiming(null, data));
  }, []);

  const resendWaitSeconds = timing.resendAvailableAt
    ? Math.ceil((timing.resendAvailableAt.getTime() - now) / 1000)
    : 0;
  const otpExpiresSeconds = timing.otpExpiresAt
    ? Math.ceil((timing.otpExpiresAt.getTime() - now) / 1000)
    : 0;

  const canResend =
    isEmail &&
    !resendLoading &&
    !loading &&
    resendWaitSeconds <= 0 &&
    (timing.resendsRemaining === null || timing.resendsRemaining > 0);

  const handleDigitChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = [...otpDigits];
    for (let i = 0; i < 6; i++) {
      next[i] = pasted[i] || "";
    }
    setOtpDigits(next);
    const focusIndex = Math.min(pasted.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = otpDigits.join("");
    if (code.length !== 6) return;
    onVerify(code);
  };

  const handleResend = async () => {
    const token = activeChallenge?.MfaSessionToken ?? activeChallenge?.mfaSessionToken;
    if (!token || !canResend) return;

    setResendLoading(true);
    setResendMessage("");
    setResendError(false);
    try {
      const response = await resendMfaEmailOtp(token);
      if (response.success === 1) {
        applyResendTiming(response.data);
        setResendMessage(
          response.message ||
            "A new code has been sent. Enter the latest code from your email."
        );
        setOtpDigits(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        if (response.data) applyResendTiming(response.data);
        setResendError(true);
        setResendMessage(response.message || "Could not resend code.");
      }
    } catch {
      setResendError(true);
      setResendMessage("Could not resend code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSwitchMethod = async (targetMethod) => {
    const token = activeChallenge?.MfaSessionToken ?? activeChallenge?.mfaSessionToken;
    if (!token || switchLoading || loading) return;

    setSwitchLoading(true);
    setResendMessage("");
    setResendError(false);
    try {
      const response = await switchMfaLoginMethod(token, targetMethod);
      if (response.success === 1 && response.data) {
        const merged = { ...activeChallenge, ...response.data };
        const needsSetup =
          merged.NeedsAuthenticatorSetup === true ||
          merged.needsAuthenticatorSetup === true;
        if (needsSetup && onSetupRequired) {
          onSetupRequired(merged);
          return;
        }
        setLocalChallenge(merged);
        setTiming(pickTiming(merged));
        if (onChallengeUpdate) onChallengeUpdate(merged);
        setOtpDigits(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        setResendMessage(response.message || "Verification method updated.");
        setResendError(false);
      } else {
        setResendError(true);
        setResendMessage(response.message || "Could not switch verification method.");
      }
    } catch {
      setResendError(true);
      setResendMessage("Could not switch verification method.");
    } finally {
      setSwitchLoading(false);
    }
  };

  const showSwitchToAuthenticator = isEmail;
  const showSwitchToEmail = !isEmail;

  return (
    <div className="mfa-verification">
      <div className="mfa-verification-header">
        <button type="button" className="mfa-back-btn" onClick={onBack} disabled={loading}>
          <i className="fas fa-arrow-left" aria-hidden="true" />
        </button>
        <h2 className="mfa-verification-title">Two-factor verification</h2>
      </div>

      <p className="mfa-verification-desc">
        {isEmail ? (
          <>
            Enter the 6-digit code sent to <strong>{maskedEmail}</strong>
          </>
        ) : (
          <>
            Open <strong>Google Authenticator</strong> or{" "}
            <strong>Microsoft Authenticator</strong> and enter the 6-digit code
            for your account.
          </>
        )}
      </p>

      {isEmail && timing.otpExpiresAt && (
        <p className="mfa-timing-hint" aria-live="polite">
          {otpExpiresSeconds > 0 ? (
            <>Code expires in {formatCountdown(otpExpiresSeconds)}</>
          ) : (
            <>Code expired — request a new one or sign in again.</>
          )}
        </p>
      )}

      {error && (
        <div className="login2-alert login2-alert-danger" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mfa-otp-inputs" onPaste={handlePaste}>
          {otpDigits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className="mfa-otp-input"
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              disabled={loading}
              autoComplete="one-time-code"
            />
          ))}
        </div>

        <button
          type="submit"
          className="login2-btn-signin mfa-verify-btn"
          disabled={loading || otpDigits.some((d) => !d)}
        >
          {loading ? (
            <>
              <span className="login2-spinner"></span>
              Verifying...
            </>
          ) : (
            "Verify & sign in"
          )}
        </button>
      </form>

      {isEmail && (
        <div className="mfa-resend-section">
          {timing.resendsRemaining === 0 ? (
            <p className="mfa-resend-limit">Resend limit reached. Please sign in again.</p>
          ) : (
            <>
              <button
                type="button"
                className="mfa-resend-link"
                onClick={handleResend}
                disabled={!canResend}
              >
                {resendLoading
                  ? "Sending..."
                  : resendWaitSeconds > 0
                    ? `Resend code in ${formatCountdown(resendWaitSeconds)}`
                    : "Resend code"}
              </button>
              {timing.resendsRemaining != null && timing.maxResends != null && (
                <p className="mfa-resend-remaining">
                  {timing.resendsRemaining} of {timing.maxResends} resends remaining
                </p>
              )}
            </>
          )}
          {resendMessage && (
            <p className={`mfa-resend-message${resendError ? " mfa-resend-message-error" : ""}`}>
              {resendMessage}
            </p>
          )}
        </div>
      )}

      {(showSwitchToAuthenticator || showSwitchToEmail) && (
        <div className="mfa-switch-section">
          <button
            type="button"
            className="mfa-switch-link"
            disabled={switchLoading || loading}
            onClick={() =>
              handleSwitchMethod(
                showSwitchToAuthenticator ? MFA_METHOD_AUTHENTICATOR : MFA_METHOD_EMAIL,
              )
            }
          >
            {switchLoading
              ? "Switching…"
              : showSwitchToAuthenticator
                ? "Use authenticator app instead"
                : "Email me a verification code instead"}
          </button>
        </div>
      )}
    </div>
  );
}

export default MfaVerification;
